import { useState, useCallback } from 'react';
import { 
  parseSupabaseError, 
  withRetry, 
  logError, 
  AppErrorClass,
  ERROR_CODES 
} from '../utils/errorHandling';

interface UseErrorHandlerOptions {
  defaultRetries?: number;
  defaultDelay?: number;
  onError?: (error: Error) => void;
}

interface UseErrorHandlerReturn {
  error: Error | null;
  isRetrying: boolean;
  clearError: () => void;
  handleError: (error: unknown, context?: Record<string, unknown>) => void;
  executeWithErrorHandling: <T>(
    operation: () => Promise<T>,
    options?: {
      retries?: number;
      delay?: number;
      context?: Record<string, unknown>;
    }
  ) => Promise<T | null>;
  retryLastOperation: () => Promise<void>;
}

export function useErrorHandler(options: UseErrorHandlerOptions = {}): UseErrorHandlerReturn {
  const { defaultRetries = 3, defaultDelay = 1000, onError } = options;
  
  const [error, setError] = useState<Error | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastOperation, setLastOperation] = useState<{
    operation: () => Promise<unknown>;
    options?: {
      retries?: number;
      delay?: number;
      context?: Record<string, unknown>;
    };
  } | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((error: unknown, context?: Record<string, unknown>) => {
    const appError = error instanceof AppErrorClass 
      ? error 
      : parseSupabaseError(error);

    logError(appError, context);
    setError(appError);
    
    if (onError) {
      onError(appError);
    }
  }, [onError]);

  const executeWithErrorHandling = useCallback(async <T,>(
    operation: () => Promise<T>,
    operationOptions: {
      retries?: number;
      delay?: number;
      context?: Record<string, unknown>;
    } = {}
  ): Promise<T | null> => {
    const { retries = defaultRetries, delay = defaultDelay, context } = operationOptions;
    
    // Store the operation for potential retry
    setLastOperation({ operation, options: operationOptions });
    
    try {
      clearError();
      setIsRetrying(false);
      
      const result = await withRetry(operation, retries, delay);
      return result;
    } catch (err) {
      handleError(err, context);
      return null;
    }
  }, [defaultRetries, defaultDelay, clearError, handleError]);

  const retryLastOperation = useCallback(async () => {
    if (!lastOperation) {
      return;
    }

    const { operation, options = {} } = lastOperation;
    const { retries = defaultRetries, delay = defaultDelay, context } = options;

    try {
      setIsRetrying(true);
      clearError();
      
      await withRetry(operation, retries, delay);
    } catch (err) {
      handleError(err, context);
    } finally {
      setIsRetrying(false);
    }
  }, [lastOperation, defaultRetries, defaultDelay, clearError, handleError]);

  return {
    error,
    isRetrying,
    clearError,
    handleError,
    executeWithErrorHandling,
    retryLastOperation,
  };
}

// Specialized hook for data fetching with loading states
interface UseAsyncOperationOptions<T> extends UseErrorHandlerOptions {
  initialData?: T;
  onSuccess?: (data: T) => void;
}

interface UseAsyncOperationReturn<T> extends UseErrorHandlerReturn {
  data: T | null;
  loading: boolean;
  execute: (
    operation: () => Promise<T>,
    options?: {
      retries?: number;
      delay?: number;
      context?: Record<string, unknown>;
    }
  ) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useAsyncOperation<T>(
  options: UseAsyncOperationOptions<T> = {}
): UseAsyncOperationReturn<T> {
  const { initialData = null, onSuccess } = options;
  const errorHandler = useErrorHandler(options);
  
  const [data, setData] = useState<T | null>(initialData);
  const [loading, setLoading] = useState(false);
  const [currentOperation, setCurrentOperation] = useState<{
    operation: () => Promise<T>;
    options?: {
      retries?: number;
      delay?: number;
      context?: Record<string, unknown>;
    };
  } | null>(null);

  const execute = useCallback(async (
    operation: () => Promise<T>,
    operationOptions?: {
      retries?: number;
      delay?: number;
      context?: Record<string, unknown>;
    }
  ) => {
    setCurrentOperation({ operation, options: operationOptions });
    setLoading(true);
    
    try {
      const result = await errorHandler.executeWithErrorHandling(operation, operationOptions);
      
      if (result !== null) {
        setData(result);
        if (onSuccess) {
          onSuccess(result);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [errorHandler, onSuccess]);

  const refetch = useCallback(async () => {
    if (!currentOperation) {
      return;
    }

    await execute(currentOperation.operation, currentOperation.options);
  }, [currentOperation, execute]);

  return {
    ...errorHandler,
    data,
    loading,
    execute,
    refetch,
  };
}

// Hook for handling form submissions with validation
interface UseFormErrorHandlerOptions extends UseErrorHandlerOptions {
  onValidationError?: (errors: Record<string, string>) => void;
}

interface UseFormErrorHandlerReturn extends UseErrorHandlerReturn {
  validationErrors: Record<string, string>;
  clearValidationErrors: () => void;
  setValidationError: (field: string, message: string) => void;
  executeFormSubmission: <T>(
    operation: () => Promise<T>,
    options?: {
      retries?: number;
      delay?: number;
      context?: Record<string, unknown>;
      validate?: () => Record<string, string> | null;
    }
  ) => Promise<T | null>;
}

export function useFormErrorHandler(
  options: UseFormErrorHandlerOptions = {}
): UseFormErrorHandlerReturn {
  const { onValidationError } = options;
  const errorHandler = useErrorHandler(options);
  
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  const setValidationError = useCallback((field: string, message: string) => {
    setValidationErrors(prev => ({ ...prev, [field]: message }));
  }, []);

  const executeFormSubmission = useCallback(async <T,>(
    operation: () => Promise<T>,
    operationOptions: {
      retries?: number;
      delay?: number;
      context?: Record<string, unknown>;
      validate?: () => Record<string, string> | null;
    } = {}
  ): Promise<T | null> => {
    const { validate, ...restOptions } = operationOptions;
    
    // Clear previous validation errors
    clearValidationErrors();
    
    // Run validation if provided
    if (validate) {
      const errors = validate();
      if (errors && Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        if (onValidationError) {
          onValidationError(errors);
        }
        
        // Create a validation error
        const validationError = new AppErrorClass(
          ERROR_CODES.VALIDATION_FAILED,
          'Please correct the errors below',
          { validationErrors: errors }
        );
        
        errorHandler.handleError(validationError, restOptions.context);
        return null;
      }
    }

    return errorHandler.executeWithErrorHandling(operation, restOptions);
  }, [clearValidationErrors, onValidationError, errorHandler]);

  return {
    ...errorHandler,
    validationErrors,
    clearValidationErrors,
    setValidationError,
    executeFormSubmission,
  };
}